import React, { useMemo } from 'react';

import { axisScale, curve, referenceMarks, scaleBounds, valueLabels, xAxisName, xLabelSpacing, xTitleRoom, yAxisName } from '../axisStyle';
import { formatAxis, formatValue, memberLabel, pointLabel, unitName } from '../format';
import type { Palette } from '../palette';
import { overrideColor, seriesOverrides } from '../seriesStyle';
import type { Block, Envelope, WindowOut } from '../types';
import Chart, { type ChartOption } from './Chart';
import type { LegendItem } from './Legend';
import Trend from './Trend';

type CategoryLineProps = { block: Block; kind: 'line' | 'area' | 'column'; envelope: Envelope; lensWindow: WindowOut; palette: Palette; width: number };

const MONTH_LABEL = /^[A-Z][a-z]{2} \d{4}$/;
const END_LABEL_ROOM = 96;

function naturalOrder(keys: Array<string>): Array<string> {
  if (keys.every((key) => MONTH_LABEL.test(key))) return [...keys].sort((a, b) => Date.parse(`1 ${a}`) - Date.parse(`1 ${b}`));
  return [...keys].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export function categoryLineOption(envelope: Envelope, palette: Palette, kind: CategoryLineProps['kind'], stacked = false, block?: Block): ChartOption | undefined {
  const overrides = seriesOverrides(block);
  const scale = axisScale(block);
  const measures = envelope.columns.filter((column) => column.role === 'measure');
  const measure = measures[0];
  const dims = envelope.columns.filter((column) => column.role === 'dim');
  const [series, axis] = dims.length === 2 ? dims : [undefined, dims[0]];
  if (!measure || !axis || dims.length > 2) return undefined;
  const area = kind === 'area';
  const bars = kind === 'column';
  const perMeasure = !series && measures.length > 1;
  const crossed = Boolean(series) && measures.length > 1;
  const crossedName = (column: string, member: string) => `${column}\u0000${member}`;
  const cells = new Map<string, number | null>();
  for (const row of envelope.rows)
    for (const column of perMeasure || crossed ? measures : [measure]) {
      const key = `${crossed ? crossedName(column.name, String(row[series!.name])) : perMeasure ? column.name : series ? String(row[series.name]) : ''}|${String(row[axis.name])}`;
      if (cells.has(key)) return undefined;
      cells.set(key, row[column.name] as number | null);
    }
  const ticks = naturalOrder([...new Set(envelope.rows.map((row) => String(row[axis.name])))]);
  const timestamp = ticks.length > 0 && ticks.every((tick) => /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(tick));
  const members = series ? naturalOrder([...new Set(envelope.rows.map((row) => String(row[series.name])))]) : [];
  const names = crossed ? measures.flatMap((column) => members.map((member) => crossedName(column.name, member))) : perMeasure ? measures.map((column) => column.name) : series ? members : [''];
  const labelled = Boolean(series) || perMeasure;
  const ended = labelled && !bars && !crossed;
  return {
    animation: false,
    grid: { left: 8, right: ended ? END_LABEL_ROOM : 8, top: 16, bottom: 8 + xTitleRoom(block), containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: palette.surface,
      borderColor: palette.hairlineStrong,
      textStyle: { color: palette.ink, fontSize: 12, fontFamily: palette.sans }, confine: true,
      valueFormatter: (value: number) => formatValue(value, measure.format),
    },
    xAxis: {
      type: 'category',
      triggerEvent: true,
      data: ticks,
      boundaryGap: bars,
      ...xAxisName(block, palette),
      axisLine: { lineStyle: { color: palette.hairlineStrong } },
      axisTick: { show: false },
      axisLabel: { color: palette.ink3, fontSize: 11, hideOverlap: true, formatter: timestamp ? (value: string) => pointLabel(value.replace(' ', 'T'), 'hour') : memberLabel, ...(!timestamp ? xLabelSpacing(block) : {}) },
    },
    yAxis: {
      type: 'value',
      scale: !area && !bars && !scale.zero,
      triggerEvent: true,
      ...scaleBounds(scale),
      splitNumber: 4,
      ...yAxisName(block, unitName(measure.format, palette.ink3), palette),
      axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, measure.format) },
      splitLine: { lineStyle: { color: palette.hairline } },
    },
    series: names.flatMap((name, index) => {
      const key = name || measure.name;
      if (overrides[key]?.hidden) return [];
      const color = overrideColor(overrides[key], palette, palette.series[index % palette.series.length]);
      const [crossedColumn, crossedMember] = name.split('\u0000');
      const label = overrides[key]?.label ?? (crossed ? `${measures.find((column) => column.name === crossedColumn)!.label} · ${memberLabel(crossedMember)}` : perMeasure ? measures.find((column) => column.name === name)!.label : series ? memberLabel(name) : measure.label);
      return {
        id: key,
        type: bars ? 'bar' : 'line',
        name: label,
        ...(stacked && bars && labelled && measures.every((column) => column.additive !== false && column.format !== 'pct') ? { stack: 'total' } : {}),
        data: ticks.map((tick) => cells.get(`${name}|${tick}`) ?? null),
        showSymbol: false,
        lineStyle: { width: 1.75, color },
        itemStyle: { color },
        ...(area ? { areaStyle: { color, opacity: 0.08 } } : {}),
        endLabel: { show: ended, formatter: () => label, color, fontSize: 11, fontFamily: palette.sans, distance: 6 },
        labelLayout: { moveOverlap: 'shiftY' },
        ...(index === 0 ? referenceMarks(block, palette, measure.format) : {}),
        ...valueLabels(block, ticks.map((tick) => cells.get(`${name}|${tick}`) ?? null), measure.format, palette),
        ...(bars ? {} : curve(block)),
      };
    }),
  };
}

export function categoryLegend(envelope: Envelope, palette: Palette, kind: CategoryLineProps['kind'], block?: Block): Array<LegendItem> | undefined {
  const option = categoryLineOption(envelope, palette, kind, false, block);
  const series = option?.series as Array<{ id: string; name: string; itemStyle: { color: string }; endLabel: { show: boolean } }> | undefined;
  if (!option || !series) return undefined;
  if (kind !== 'column' && series.some((entry) => entry.endLabel.show || series.length < 2)) return [];
  return series.length < 2 ? [] : series.map((entry) => ({ name: entry.name, color: entry.itemStyle.color, mark: kind === 'column' ? 'square' : 'line', part: `series:${entry.id}` }));
}

export default function CategoryLine({ block, kind, envelope, lensWindow, palette, width }: CategoryLineProps) {
  const option = useMemo(() => categoryLineOption(envelope, palette, kind, block.stack === true, block), [envelope, palette, kind, block]);
  if (!option) return <Trend block={block} envelope={envelope} kind={kind} lensWindow={lensWindow} palette={palette} width={width} />;
  return <Chart option={option} />;
}
